import type { AddressFragment, CustomerFragment } from '#graphql-operations'

/**
 * The signed-in shopper's identity, shaped exactly like the `user_data` block
 * `useAnalytics` already sends with `purchase`, so a single set of GTM tags
 * reads one structure whether it fires on a product page or at the till.
 */
export interface CustomerDataLayerAddress {
  first_name?: string
  last_name?: string
  street?: string
  city?: string
  region?: string
  country?: string
  postal_code?: string
}

export interface CustomerDataLayerPayload {
  user_id: string | null
  user_data: {
    email_address?: string
    phone_number?: string
    address?: CustomerDataLayerAddress
  } | null
}

/**
 * Pushed once the visitor is *known* to be signed out, which clears both
 * variables. Never pushed merely because the check has not finished yet - that
 * would wipe a restored snapshot a moment after it was put in place.
 */
export const SIGNED_OUT_PAYLOAD: CustomerDataLayerPayload = { user_id: null, user_data: null }

/**
 * sessionStorage, deliberately not localStorage.
 *
 * Vendure's session cookie is httpOnly, so the browser cannot tell whether a
 * persisted snapshot still belongs to the person now at the keyboard. A
 * localStorage copy outlives the 30-day session, and on a shared browser
 * profile whose cookie has since lapsed it would replay the previous shopper's
 * e-mail address into the dataLayer for whoever opens the site next - and the
 * page-view tags would record it before the background check could clear it.
 * sessionStorage dies with the tab, which removes that window entirely while
 * still covering the case this exists for: one signed-in person browsing from
 * page to page.
 */
const STORAGE_KEY = 'customer-datalayer'

/** `null` means "nothing usable stored", including storage being unreadable. */
export function readCustomerSnapshot(): CustomerDataLayerPayload | null {
  if (!import.meta.client)
    return null

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw)
      return null

    const parsed = JSON.parse(raw) as CustomerDataLayerPayload
    // Anything without an id is not worth pushing, and guards against a
    // half-written or hand-edited entry.
    return parsed?.user_id ? parsed : null
  }
  catch {
    // Hardened profiles and Safari's private mode throw on the first touch;
    // malformed JSON lands here too.
    return null
  }
}

export function writeCustomerSnapshot(payload: CustomerDataLayerPayload): void {
  if (!import.meta.client)
    return

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  }
  catch {
    // Storage unavailable - the visit still works, it just starts each page
    // without the instant copy.
  }
}

export function clearCustomerSnapshot(): void {
  if (!import.meta.client)
    return

  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  }
  catch {
    // Nothing to do: if it cannot be removed it could not have been written.
  }
}

/**
 * Maps a saved address onto the dataLayer shape. Names come from the customer,
 * which is where `purchase` takes them from too - `Address` only carries a
 * combined `fullName`.
 */
export function toDataLayerAddress(
  customer: CustomerFragment,
  address: AddressFragment,
): CustomerDataLayerAddress {
  return {
    first_name: customer.firstName,
    last_name: customer.lastName,
    street: address.streetLine1 ?? undefined,
    city: address.city ?? undefined,
    region: address.province ?? undefined,
    // The country *name*, matching what `purchase` sends (OrderAddress.country
    // is already the name, not the code).
    country: address.country?.name,
    postal_code: address.postalCode ?? undefined,
  }
}

export function buildCustomerDataLayerPayload(
  customer: CustomerFragment,
  address: CustomerDataLayerAddress | null,
): CustomerDataLayerPayload {
  return {
    user_id: String(customer.id),
    user_data: {
      email_address: customer.emailAddress,
      phone_number: customer.phoneNumber ?? undefined,
      ...(address ? { address } : {}),
    },
  }
}
